/**
 * Design Zone storefront event bridge.
 *
 * Emits a small set of named events into the analytics this store ALREADY runs. It adds no new
 * analytics platform, no app, and no Shopify setting:
 *   1. gtag('event', ...) - the Google tag already on every page (GA4 G-QX84K05BL8, loaded by the
 *      Google & YouTube channel pixel). This is the channel the owner can read today with no admin
 *      change. There is no Google Tag Manager container on this store, so a dataLayer push would be
 *      inert and is deliberately not made.
 *   2. Shopify.analytics.publish - Shopify's first-party custom storefront event, delivered to any
 *      Web Pixel that subscribes to it (Settings > Customer events). Shopify's documented naming
 *      rule for these is a colon namespace, so they go out as dz:<event>.
 *   3. a DOM CustomEvent of the same name, so anything else on the page can listen without polling.
 *
 * Loaded deferred, before the deferred module scripts that use it, so window.dzTrack exists first.
 * Every caller still guards with window.dzTrack?.(...).
 */
(function () {
  if (window.dzTrack) return;

  var MAX_LOG = 50;
  var PARAM = 'dz';
  var GA4_MEASUREMENT_ID = 'G-QX84K05BL8'; // the store's own GA4 property, already loaded by gtag.js
  // Only these reach GA4. dz_timestamp and dz_page_path are deliberately left out: GA4 stamps its own
  // timestamp and page_location, and every extra parameter costs one of the 50 custom-dimension slots.
  var GA4_PARAMS = ['dz_locale', 'dz_device', 'dz_source', 'dz_method', 'dz_opened_from', 'dz_surface', 'dz_stage', 'dz_trigger', 'dz_continued_to_checkout', 'dz_referrer_confirms_checkout'];

  // The Google tag is injected by an app pixel and is often not defined yet when the cart-arrival
  // events fire, so GA4 hits are held briefly and flushed as soon as gtag appears. Pre-seeding
  // window.dataLayer instead would risk the hit being processed before gtag.js runs its own config.
  var pendingGa4 = [];
  var ga4Timer = null;

  /**
   * The Google tag existing is not enough: gtag.js throws if an event names a measurement id whose
   * config has not run yet, so wait until the GA4 container itself is registered.
   */
  function ga4Ready() {
    try {
      return typeof window.gtag === 'function' && !!(window.google_tag_manager && window.google_tag_manager[GA4_MEASUREMENT_ID]);
    } catch (error) {
      return false;
    }
  }

  function flushGa4() {
    if (!ga4Ready()) return false;
    while (pendingGa4.length) {
      var item = pendingGa4.shift();
      try {
        window.gtag('event', item.name, item.params);
        if (item.channels) item.channels.gtag = 'sent';
      } catch (error) {}
    }
    return true;
  }

  function sendGa4(name, params, channels) {
    pendingGa4.push({ name: name, params: params, channels: channels });
    if (flushGa4()) return 'sent';
    if (!ga4Timer) {
      var tries = 0;
      ga4Timer = window.setInterval(function () {
        tries += 1;
        if (flushGa4() || tries > 60) {
          window.clearInterval(ga4Timer);
          ga4Timer = null;
        }
      }, 250);
    }
    return 'queued';
  }

  /** Shopify's own consent gate. Saudi traffic is unrestricted, but a consent-required visitor is not. */
  function analyticsAllowed() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (privacy && typeof privacy.analyticsProcessingAllowed === 'function') {
        return privacy.analyticsProcessingAllowed() !== false;
      }
    } catch (error) {}
    return true;
  }

  function context() {
    var lang = String(document.documentElement.lang || 'en').toLowerCase();
    var mobile = false;
    try {
      mobile = window.matchMedia('(max-width: 749px)').matches;
    } catch (error) {
      mobile = window.innerWidth < 750;
    }
    return {
      dz_locale: lang.indexOf('ar') === 0 ? 'ar' : 'en',
      dz_device: mobile ? 'mobile' : 'desktop',
      dz_page_path: window.location.pathname,
      dz_timestamp: new Date().toISOString(),
    };
  }

  // ?dz_debug=1 logs every event to the console, so the owner can watch them without a tag manager.
  try {
    if (new URLSearchParams(window.location.search).get('dz_debug') === '1') window.__dzTrackDebug = true;
  } catch (error) {}

  /**
   * Emits one event.
   * @param {string} name - event name, always prefixed dz_
   * @param {Object} [detail] - extra fields, merged over the automatic context
   */
  window.dzTrack = function dzTrack(name, detail) {
    // Nothing is reported from the theme editor: those are the merchant's own clicks.
    if (window.Shopify && window.Shopify.designMode) return null;
    if (!analyticsAllowed()) return null;

    var payload = context();
    if (detail) {
      for (var key in detail) {
        if (Object.prototype.hasOwnProperty.call(detail, key)) payload[key] = detail[key];
      }
    }

    // Which channels actually accepted the event, so QA can tell a missing tag from a broken call.
    var channels = { gtag: 'unavailable', shopify: false };

    try {
      // send_to pins the hit to GA4 so it never reaches the Google Ads tags on the same gtag.
      var ga4 = { send_to: GA4_MEASUREMENT_ID };
      for (var i = 0; i < GA4_PARAMS.length; i++) {
        var key = GA4_PARAMS[i];
        if (payload[key] !== undefined) ga4[key] = String(payload[key]);
      }
      channels.gtag = sendGa4(name, ga4, channels);
    } catch (error) {}

    try {
      var analytics = window.Shopify && window.Shopify.analytics;
      if (analytics && typeof analytics.publish === 'function') {
        channels.shopify = analytics.publish('dz:' + name.replace(/^dz_/, ''), payload) !== false;
      }
    } catch (error) {}

    try {
      document.dispatchEvent(new CustomEvent(name, { detail: payload }));
    } catch (error) {}

    window.dzTrackLog = window.dzTrackLog || [];
    window.dzTrackLog.push({ name: name, payload: payload, channels: channels });
    if (window.dzTrackLog.length > MAX_LOG) window.dzTrackLog.shift();
    if (window.__dzTrackDebug) console.info('[dz]', name, payload, channels);

    return payload;
  };

  /** True when the previous page was the Shopify checkout. */
  function cameFromCheckout() {
    try {
      var referrer = new URL(document.referrer);
      return referrer.origin === window.location.origin && /\/checkouts?\//.test(referrer.pathname);
    } catch (error) {
      return false;
    }
  }

  /**
   * Records the arrival on the cart from checkout.
   *
   * Theme JavaScript cannot run inside Shopify's hosted checkout on the Advanced plan, so the click
   * on "Edit or Remove Items" is recorded on the very next page instead: the order-summary link
   * carries ?dz=edit-items, which makes that click exact rather than inferred. The cart icon in the
   * checkout header carries no parameter, so it is recognised by its referrer alone.
   */
  function recordCartEntry() {
    if (!/\/cart\/?$/.test(window.location.pathname)) return;
    var params = new URLSearchParams(window.location.search);
    var flag = params.get(PARAM);
    var fromCheckout = cameFromCheckout();

    if (flag === 'edit-items') {
      window.dzTrack('dz_edit_remove_items_click', {
        dz_source: 'checkout_order_summary',
        dz_referrer_confirms_checkout: fromCheckout,
      });
    } else if (fromCheckout) {
      window.dzTrack('dz_edit_remove_items_click', {
        dz_source: 'checkout_header',
        dz_referrer_confirms_checkout: true,
      });
    }

    if (flag === 'edit-items' || fromCheckout) {
      window.dzTrack('dz_cart_return_from_checkout', {
        dz_source: flag === 'edit-items' ? 'checkout_order_summary' : 'checkout_header',
      });
      document.documentElement.setAttribute('data-dz-from-checkout', '');
    }

    if (flag) {
      // Take the flag out of the address bar once it has been read, so a reload, a back navigation or
      // a bookmark of this URL cannot record the same click again. The attribute set just above is
      // what the cart's return message keys on, so the message survives.
      try {
        params.delete(PARAM);
        var query = params.toString();
        window.history.replaceState(null, '', window.location.pathname + (query ? '?' + query : '') + window.location.hash);
      } catch (error) {}
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', recordCartEntry, { once: true });
  } else {
    recordCartEntry();
  }
})();
